/**
 * Auth Navigation Hook
 *
 * Handles automatic redirect for protected routes.
 * Stores intended path for post-login redirect.
 */

import { useEffect, useRef } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { LogtoSignInOptions } from '@lib/logto';
import { userInitiatedSignInSlot } from '@utils/crossTabAuthSync/authFlowFlags';
import {
  getAuthCallbackUrl,
  storeRedirectPath,
  isUserCancellation,
  isAuthRoute,
  normalizeRoutePath,
} from '@utils/auth';
import { hasAuthIntent } from '@utils/authIntent';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { logger } from '@utils/logger';
import { AUTH_DOOR_PATH } from '@lib/magicLink/authLinkParams';
import { shouldRouteToDoor } from '@lib/magicLink/doorRouting';
import { AuthSessionState } from './authTypes';

// =============================================================================
// PUBLIC ROUTE MATCHERS
// =============================================================================

/**
 * AIDEV-NOTE: Public routes that guest users can access without authentication
 *
 * AIDEV-NOTE [MEETBALL magic-link]: these are CASE-SENSITIVE regexes, and
 * `isPublicRoute` tests them against `normalizeRoutePath(pathname)`, not the raw
 * string. That normalization is what makes them agree with React Router (which
 * matches case-insensitively and ignores a trailing slash) and with
 * `captureAuthLinkParams`, which normalizes identically. Write new matchers in
 * lower case with no trailing slash.
 *
 * /auth and /auth/link are here because AuthProvider's session-expired-drawer
 * dismissal bounces the visitor to /home whenever `isPublicRoute` is false: a
 * stale-session magic-link arrival was being thrown straight off the door.
 */
const PUBLIC_ROUTE_MATCHERS = [
  /^\/auth$/, // Magic-link door (guest entry point)
  /^\/auth\/link$/, // Magic-link arrival
  /^\/home$/, // Home feed (guest browsing)
  /^\/create-question$/, // Create question (guest flow)
  /^\/events$/, // Events list
  /^\/events\/[^/]+$/, // Event details (excluding edit/big-screen)
  /^\/events\/[^/]+\/big-screen$/, // Big screen (existing)
  /^\/questions\/[^/]+$/, // Question details (guest access to public questions)
  /^\/communities\/[^/]+$/, // Community detail (guest preview)
  /^\/communities\/[^/]+\/welcome(-legacy|_2)?$/, // Space welcome/intro landing + parked V1 (-legacy) + alias (_2); guest-accessible, CTA gates auth
  /^\/kit$/, // Organizer Activation Kit entry gate; unauthenticated by design
  /^\/kit\/[^/]+$/, // Organizer Activation Kit per-Space (redirects to /communities/:slug/kit)
  /^\/communities\/[^/]+\/kit$/, // The Activation Kit's address (/kit/:slug redirects here). Public Spaces stay open signed out; invite-only and private Spaces reuse the Space page's gates
];

/**
 * Check if a path is a public route.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: normalizes first — see the note on
 * PUBLIC_ROUTE_MATCHERS for why the raw pathname is the wrong thing to test.
 */
export function isPublicRoute(pathname: string): boolean {
  const normalized = normalizeRoutePath(pathname);
  return PUBLIC_ROUTE_MATCHERS.some(regex => regex.test(normalized));
}

// =============================================================================
// HOOK OPTIONS
// =============================================================================

export interface UseAuthNavigationOptions {
  /** Whether user is authenticated with Logto */
  logtoIsAuthenticated: boolean;
  /** Whether auth state is still loading */
  logtoIsLoading: boolean;
  /** Whether user data is still loading */
  userLoading: boolean;
  /**
   * Current AuthProvider session state. The auto-guard suppresses `logtoSignIn()`
   * while `REAUTHENTICATING` or `SESSION_EXPIRED` so it cannot race with the
   * recovery flow in `useInvalidGrantHandler`.
   */
  sessionState: AuthSessionState;
  /** Logto SDK signIn as AuthProvider wraps it: clears the "Signed in as ..."
   * cue before starting the sign-in (see AuthProvider's `logtoSignIn`). */
  logtoSignIn: (options: LogtoSignInOptions) => Promise<void>;
  /** React Router navigate, used to route the auto-guard through the /auth
   * door when `featureFlags.magicLinkAuth` is on (mirrors useSignInOut's
   * `signIn`; see the AIDEV-NOTE below). */
  navigate: NavigateFunction;
  /** Analytics tracking function */
  trackEvent: (event: string, properties?: Record<string, unknown>) => void;
}

// =============================================================================
// HOOK IMPLEMENTATION
// =============================================================================

/**
 * Manages automatic navigation for authentication.
 * Redirects unauthenticated users on protected routes to sign-in.
 */
export function useAuthNavigation(options: UseAuthNavigationOptions): void {
  const {
    logtoIsAuthenticated,
    logtoIsLoading,
    userLoading,
    sessionState,
    logtoSignIn,
    navigate,
    trackEvent,
  } = options;

  // Prevent concurrent navigation attempts
  const navigationInProgress = useRef(false);

  // AIDEV-NOTE [MEETBALL magic-link]: `navigate` is held in a ref and kept out of
  // the effect's dep array. react-router 6.30 memoises useNavigate on
  // `locationPathname`, so the function gets a NEW identity on every client-side
  // navigation; with it in the deps this auto-guard re-ran on every route change
  // and could redirect a guest off a protected page even with the magic-link flag
  // off. The guard must only re-run on auth-state changes. AuthProvider now
  // passes `useStableNavigate()`, so that identity no longer moves in
  // production, but the ref stays: this hook takes `navigate` from its caller
  // and the correctness argument above is about what the effect is allowed to
  // re-run on, not about who happens to be calling it today. Assigned during
  // render so the ref is live for the effect that runs immediately after it.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    // Wait for auth/user loading
    if (logtoIsLoading || userLoading) {
      return;
    }

    const currentPath = globalThis.location.pathname;
    const currentSearch = globalThis.location.search;
    const currentHash = globalThis.location.hash;

    const hasIntendedAction = hasAuthIntent();

    const isPublic = isPublicRoute(currentPath);

    // AIDEV-NOTE: Issue #567 - Suppress the auto-guard while the recovery flow
    // is active. Without this, the guard sees !logtoIsAuthenticated after
    // useInvalidGrantHandler clears tokens and fires a competing logtoSignIn(),
    // producing the redirect loop.
    const isSessionRecovering =
      sessionState === AuthSessionState.REAUTHENTICATING ||
      sessionState === AuthSessionState.SESSION_EXPIRED;

    const shouldInitiateAuth =
      !logtoIsAuthenticated &&
      !isAuthRoute(currentPath) &&
      !isPublic &&
      !navigationInProgress.current &&
      !isSessionRecovering;

    if (shouldInitiateAuth) {
      navigationInProgress.current = true;

      // AIDEV-NOTE: Use /home as landing since it's now guest-friendly
      const intendedPath = currentPath === '/' ? '/home' : currentPath;
      storeRedirectPath(intendedPath, currentSearch, currentHash);

      // AIDEV-NOTE: Removed postRedirectUri to prevent Logto from redirecting before
      // our Callback.tsx can check if user needs to complete SignupFlow.
      // AIDEV-NOTE [MEETBALL magic-link]: the door-routing gate is
      // `shouldRouteToDoor` in @lib/magicLink/doorRouting -- ONE expression,
      // shared with useSignInOut's signIn callback, in a leaf module that
      // imports only featureFlags and isNativePlatform (the two things this file
      // already imported to spell it out by hand). It used to be written out
      // here and there with a CHANGE ONE, CHANGE THE OTHER note over each; see
      // that module for why the api-client concern that justified the duplicate
      // did not apply. This auto-guard takes no options, so it passes no
      // `bypassDoor`: there is nothing here that could be the door's own call.
      async function initiateLogtoSigninFlow() {
        const callbackUrl = getAuthCallbackUrl();
        const routeToDoor = shouldRouteToDoor();
        try {
          trackEvent(ANALYTICS_EVENTS.auth_initiated, {
            entrypoint: routeToDoor ? 'auth_door' : 'auto_guard',
            intended_path: intendedPath,
            has_intended_action: hasIntendedAction,
            reason: 'protected_route',
          });

          // Mark as user-initiated so other tabs get notified after sign-in completes
          userInitiatedSignInSlot.write(true);

          if (routeToDoor) {
            // AIDEV-NOTE [MEETBALL magic-link]: the cross-tab mirrors used to be
            // written here too. They now live in AuthDoor's own mount effect, so
            // that ARRIVING at the door refreshes them however the visitor got
            // there. See the note on that effect.
            // AIDEV-NOTE [MEETBALL magic-link]: `replace` is load-bearing here,
            // not tidiness. A push leaves the protected route in history, and
            // Back restores it CLIENT-SIDE: this effect's deps are all auth
            // state (currentPath is read in the body, not declared), so it never
            // re-runs, ProtectedRoute renders null for a signed-out visitor, and
            // the tab is a blank dead end for the life of the document. The
            // legacy path could not hit that -- `await logtoSignIn(...)` was a
            // whole-document navigation, so Back always re-evaluated the module
            // and re-armed the guard. Replacing removes the entry entirely.
            navigateRef.current(AUTH_DOOR_PATH, { replace: true });
            return;
          }

          await logtoSignIn({
            redirectUri: callbackUrl,
            firstScreen: 'register',
          });
        } catch (err: unknown) {
          if (!isUserCancellation(err)) {
            logger.error('Auto-guard sign-in failed:', err);
          }
        } finally {
          navigationInProgress.current = false;
        }
      }

      void initiateLogtoSigninFlow();
    }

    // AIDEV-NOTE: Partial auth state (Logto tokens but no profile) is now handled in CreateQuestion
    // When user submits a question, we detect partial auth and redirect to /signup directly
    // instead of going through Logto again. This provides a smoother UX.
    // `navigate` is deliberately absent: it lives in navigateRef (see above).
  }, [logtoIsAuthenticated, logtoIsLoading, logtoSignIn, sessionState, trackEvent, userLoading]);
}
